import React, { useEffect, useRef } from 'react';
import { plural, t } from '@lingui/macro';

import { Box } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import useSearchByName from './hooks/useSearchByName';
import ModalSearchScreen from './screens/ModalSearchScreen';
import SearchByNameLoginRequiredScreen from './screens/SearchByNameLoginRequiredScreen';

type Props = {
  initialLocation?: string;
};

const SearchByNameContent = ({ initialLocation }: Props): JSX.Element => {
  const { isAuthenticated, isLoading: isAuthLoading } = useCurrentUser();
  const { state, search } = useSearchByName();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const wasLoadingRef = useRef(false);

  const trimmedInitialLocation = initialLocation?.trim() || '';

  useEffect(() => {
    if (state.isLoading && !wasLoadingRef.current) {
      setScreenReaderAnnouncement(t`Searching for sitters`);
      wasLoadingRef.current = true;
      return;
    }
    if (!state.isLoading && wasLoadingRef.current) {
      wasLoadingRef.current = false;
      if (state.error) {
        setScreenReaderAnnouncement(state.error);
      } else if (state.results !== null) {
        const count = state.results.length;
        // English lacks a CLDR "zero" plural form, so `plural()` would select
        // `other` for 0 and announce "0 sitters found".
        const message =
          count === 0
            ? t`No sitters found`
            : plural(count, {
                one: '# sitter found',
                other: '# sitters found',
              });
        setScreenReaderAnnouncement(message);
      }
    }
  }, [state.isLoading, state.error, state.results, setScreenReaderAnnouncement]);

  if (isAuthLoading) {
    // Kibble Box's cross-platform role allowlist excludes "status", so we use
    // the equivalent live-region attributes directly.
    return (
      <Box
        p="4x"
        testID="search-by-name-auth-loading"
        aria-live="polite"
        aria-busy="true"
        aria-atomic="true"
      >
        <A11yHiddenBox>{t`Loading`}</A11yHiddenBox>
        <Box mb="4x">
          <LoadingShimmer width="60%" height="24px" />
        </Box>
        <Box mb="3x">
          <LoadingShimmer width="100%" height="48px" />
        </Box>
        <LoadingShimmer width="100%" height="48px" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <SearchByNameLoginRequiredScreen />;
  }

  return (
    <ModalSearchScreen
      initialLocation={trimmedInitialLocation}
      results={state.results}
      query={state.query}
      isLoading={state.isLoading}
      error={state.error}
      wasCapped={state.wasCapped}
      onSearch={search}
    />
  );
};

export default SearchByNameContent;
